'use client';

import { useEffect, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import { Button } from '@publishing/components/ui/Button';
import Icon from '@publishing/components/ui/Icon';
import { ArrowArrow, ArrowChevron, EtcPass } from '@publishing/components/ui/SvgSprite';
import { fetchClient } from '@/shared/apis';
import { GTM_EVENT } from '@/shared/constants/gtm';
import { gtmClick } from '@/shared/utils/gtm';
import { FORM_KEY } from '../_types';
import type { FormKey } from '../_types';
import styles from './FormTip.module.scss';

export type TipVariant = 'individual' | 'lounge';

interface SingleRoomBadge {
  id: number;
  name: string;
  backgroundColor: string;
}

export interface SingleRoomProduct {
  id: number;
  displayName: string;
  imageUrl: string;
  priceInfo: {
    salePrice: number;
  };
  badges: SingleRoomBadge[];
  branchDisplayName: string;
}

/** 공유오피스 — userCount 1일 때 1인실 Tip */
const SHARED_OFFICE_TIP_KEY = FORM_KEY.SHARED_OFFICE;

const TIP_VARIANT: Partial<Record<FormKey, TipVariant>> = {
  [FORM_KEY.SHARED_OFFICE]: 'individual',
  [FORM_KEY.INDIVIDUAL_OFFICE]: 'individual',
  [FORM_KEY.LOUNGE_MEMBERSHIP]: 'lounge',
};

const TIP_VARIANT_CONFIG = {
  individual: {
    copy: '1인 오피스는 결제부터 계약까지 온라인으로 할 수 있어요.',
    buttonLabel: '1인 오피스 더보기',
    buttonHref: '/location/individual',
    ariaLabel: '1인 오피스 안내',
  },
  lounge: {
    copy: '지금 바로 라운지 멤버십을 시작해 보세요',
    buttonLabel: '라운지 멤버십 시작하기',
    buttonHref: '/lounge-pass',
    ariaLabel: '라운지 멤버십 안내',
  },
} as const satisfies Record<TipVariant, { copy: string; buttonLabel: string; buttonHref: string; ariaLabel: string }>;

const LOUNGE_COMPARE_ROWS: { label: string; original: ReactNode; unlimited: ReactNode; weekend: ReactNode }[] = [
  {
    label: '이용 지점',
    original: <>오리지널 지점<br className="hidden_pc" /> 이용 가능</>,
    unlimited: <>오리지널+제휴 지점<br className="hidden_pc" /> 이용 가능</>,
    weekend: <>오리지널 지점<br className="hidden_pc" /> 이용 가능</>,
  },
  {
    label: '이용 기간',
    original: <>최대 12개월<br className="hidden_pc" /> 이용 가능</>,
    unlimited: <>최대 12개월<br className="hidden_pc" /> 이용 가능</>,
    weekend: <>최대 12개월<br className="hidden_pc" /> 이용 가능</>,
  },
  {
    label: '일일 이용', 
    original: <>1일 최대 24시간<br className="hidden_pc" /> 이용 가능</>,
    unlimited: <>1일 최대 24시간<br className="hidden_pc" /> 이용 가능</>,
    weekend: <>1일 최대 24시간<br className="hidden_pc" /> 이용 가능</>,
  },
  {
    label: '이용 시간',
    original: '00:00 ~ 23:59',
    unlimited: '00:00 ~ 23:59',
    weekend: <>평일 18:00~05:59<br />주말 00:00~23:59</>,
  },
];

function isSingleUserCount(values: Record<string, string>): boolean {
  const raw = (values.userCount ?? '').replace(/,/g, '').trim();
  if (!raw) return false;
  const n = Number(raw);
  return Number.isInteger(n) && n === 1;
}

function shouldShowTip(key: FormKey, values: Record<string, string>): boolean {
  const variant = TIP_VARIANT[key];
  if (!variant) return false;
  if (key === SHARED_OFFICE_TIP_KEY) return isSingleUserCount(values);
  return true;
}

export interface FormTipProps {
  formKey: FormKey;
  values: Record<string, string>;
  /** false면 항상 렌더하지 않음 — multi 레이아웃에서 사용 */
  enabled?: boolean;
  className?: string;
  copy?: string;
  buttonLabel?: string;
  buttonHref?: string;
  onMoreClick?: () => void;
  /** variant별 기본 콘텐츠 대신 주입 */
  content?: ReactNode;
  /**
   * 폭이 좁아 PC에도 모바일 레이아웃을 써야 하는 케이스.
   * root를 세로 스택으로 바꾸고 individual 카드를 swiper로 렌더한다.
   */
  swiper?: boolean;
}

async function fetchSingleRooms(coords?: GeolocationCoordinates): Promise<SingleRoomProduct[]> {
  try {
    const { data, error } = await fetchClient.GET('/api/v1/single-rooms', {
      params: {
        query: coords
          ? { latitude: coords.latitude, longitude: coords.longitude }
          : undefined,
      },
    });
    if (error) return [];
    return (data?.products ?? []) as SingleRoomProduct[];
  } catch {
    return [];
  }
}

function useSingleRoomProducts(enabled: boolean) {
  const [products, setProducts] = useState<SingleRoomProduct[] | null>(enabled ? null : []);

  useEffect(() => {
    if (!enabled) return;

    const load = (coords?: GeolocationCoordinates) =>
      fetchSingleRooms(coords).then(setProducts);

    if (!navigator.geolocation) {
      load();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => load(pos.coords),
      () => load(),
    );
  }, [enabled]);

  return products;
}

function SingleRoomCard({ item }: { item: SingleRoomProduct }) {
  return (
    <article className={styles.card}>
      <div className={styles.thumb}>
        <img src={item.imageUrl} alt={item.displayName} />
      </div>
      <div className={styles.card_title}>
        {item.branchDisplayName} {item.displayName}
      </div>
      <div className={styles.card_info}>
        <div className={styles.price}>
          {item.priceInfo.salePrice?.toLocaleString('ko-KR')}원
        </div>
        {item.badges.length > 0 && (
          <div className={styles.badge_list}>
            {item.badges.map((badge) => (
              <span
                key={badge.id}
                className={styles.product_badge}
                style={{ backgroundColor: badge.backgroundColor }}
              >
                {badge.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

/** 기본형 — PC 4열 grid, 모바일 scroll-snap */
function TipIndividualContent({ products }: { products: SingleRoomProduct[] }) {
  return (
    <div className={styles.tip_individual}>
      <div className={styles.cards}>
        {products.map((item) => (
          <SingleRoomCard key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

/** swiper형 — 카드 폭이 136px 밑으로 눌릴 만큼 좁아지면 스와이프 + 좌우 버튼 노출 */
function TipIndividualSwiperContent({ products }: { products: SingleRoomProduct[] }) {
  const [prevEl, setPrevEl] = useState<HTMLElement | null>(null);
  const [nextEl, setNextEl] = useState<HTMLElement | null>(null);

  return (
    <div className={styles.tip_individual}>
      <Swiper
        modules={[Navigation]}
        slidesPerView="auto"
        spaceBetween={12}
        className={styles.cards_swiper}
        navigation={prevEl && nextEl ? { prevEl, nextEl } : false}
      >
        {products.map((item) => (
          <SwiperSlide key={item.id} className={styles.card_slide}>
            <SingleRoomCard item={item} />
          </SwiperSlide>
        ))}
      </Swiper>

      <Button
        theme="icon"
        type="white"
        filled
        size="medium"
        ref={setPrevEl}
        className={`${styles.nav} ${styles.nav_prev}`}
        aria-label="이전 슬라이드"
      >
        <Icon name={ArrowChevron} direction="left" size={24} />
      </Button>
      <Button
        theme="icon"
        type="white"
        filled
        size="medium"
        ref={setNextEl}
        className={`${styles.nav} ${styles.nav_next}`}
        aria-label="다음 슬라이드"
      >
        <Icon name={ArrowChevron} direction="right" size={24} />
      </Button>
    </div>
  );
}

function TipLoungeContent() {
  return (
    <div className={styles.tip_lounge}>
      <div className={styles.compare}>
        {([
          {
            key: 'original',
            name: '오리지널 패스',
            icon: EtcPass,
            iconTheme: 'original' as const,
            colorClass: styles.original,
            items: LOUNGE_COMPARE_ROWS.map((r) => r.original), // TODO :: 더블체크 필요
          },
          {
            key: 'unlimited',
            name: '무제한 패스',
            icon: EtcPass,
            iconTheme: 'day' as const,
            colorClass: styles.unlimited,
            items: LOUNGE_COMPARE_ROWS.map((r) => r.unlimited),
          },
          {
            key: 'weekend',
            name: '주말&야간 패스',
            icon: EtcPass,
            iconTheme: 'night' as const,
            colorClass: styles.weekend,
            items: LOUNGE_COMPARE_ROWS.map((r) => r.weekend),
          },
        ] as const).map((col) => (
          <ul key={col.key} className={styles.col}>
            <li className={styles.col_head}>
              <span className={`${styles.pass_icon} ${col.colorClass}`}>
                <Icon name={col.icon} theme={col.iconTheme} size={23} aria-hidden />
              </span>
              {col.name}
            </li>
            {col.items.map((item, index) => (
              <li key={`${col.key}-${LOUNGE_COMPARE_ROWS[index].label}`} className={styles.col_item}>
                {item}
              </li>
            ))}
          </ul>
        ))}
      </div>
    </div>
  );
}

function FormTipContent({
  variant,
  className,
  copy,
  buttonLabel,
  buttonHref,
  onMoreClick,
  content,
  swiper,
}: {
  variant: TipVariant;
  className?: string;
  copy?: string;
  buttonLabel?: string;
  buttonHref?: string;
  onMoreClick?: () => void;
  content?: ReactNode;
  swiper?: boolean;
}) {
  const config = TIP_VARIANT_CONFIG[variant];
  // 카드 폭 계산이 swiper + CSS max()로 넘어가 불필요 — 복구 대비 주석 유지
  // const isNarrow = className?.includes(styles.narrow) ?? false;
  const useDefaultIndividualContent = variant === 'individual' && content == null;
  const products = useSingleRoomProducts(useDefaultIndividualContent);
  const isProductsLoading = useDefaultIndividualContent && products === null;
  const IndividualContent = swiper ? TipIndividualSwiperContent : TipIndividualContent;

  const resolvedContent =
    content ??
    (variant === 'individual' ? (
      isProductsLoading ? null : (
        <IndividualContent products={products ?? []} />
      )
    ) : (
      <TipLoungeContent />
    ));

  const resolvedCopy = copy ?? config.copy;
  const resolvedButtonLabel = buttonLabel ?? config.buttonLabel;
  const resolvedButtonHref = buttonHref ?? config.buttonHref;

  return (
    <div className={clsx(styles.root, swiper && styles.swiper, className)} aria-label={config.ariaLabel}>
      <div className={styles.head}>
        <span className={styles.badge}>TIP</span>
        <p className={styles.copy}>{resolvedCopy}</p>
      </div>
      <Button
        size="xsmall"
        href={resolvedButtonHref}
        onClick={resolvedButtonHref ? undefined : onMoreClick}
        className={styles.more_button}
        target="_blank"
        {...gtmClick(GTM_EVENT.BTN, resolvedButtonLabel, resolvedButtonHref)}
      >
        {resolvedButtonLabel}
        <Icon name={ArrowArrow} direction="right" size={16} aria-hidden />
      </Button>
      {resolvedContent}
    </div>
  );
}

export default function FormTip({
  formKey,
  values,
  enabled = true,
  className,
  copy,
  buttonLabel,
  buttonHref,
  onMoreClick,
  content,
  swiper,
}: FormTipProps) {
  if (!enabled) return null;
  if (!shouldShowTip(formKey, values)) return null;

  const variant = TIP_VARIANT[formKey];
  if (!variant) return null;

  return (
    <FormTipContent
      variant={variant}
      className={className}
      copy={copy}
      buttonLabel={buttonLabel}
      buttonHref={buttonHref}
      onMoreClick={onMoreClick}
      content={content}
      swiper={swiper}
    />
  );
}
